import { Box, Divider, Grid, TextField, Typography, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Form, Formik } from "formik";
import { postAPICall, getAPICall } from "lib/callApi";
import {
  CHECK_IF_KPI_TEMPLATE_NAME_IS_UNIQUE,
  CREATE_KPI_TEMPLATE,
  FETCH_SINGLE_KPI_TEMPLATE,
  FETCH_KPI_ACTIVITIES,
} from "lib/constants";
import EditKPITable from "pages/KPI/components/EditKPITable";
import { useState, useEffect } from "react";
import * as Yup from "yup";

function KpiTemplateModalCopy({
  handleClose,
  showNotification,
  user,
  refetchKpiEntries,
  selectedTemplateId,
  setCopying,
  isMobileDisplay,
}) {
  const [isRequestApiLoading, setIsRequestApiLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [templateDetail, setTemplateDetail] = useState({
    name: "",
    description: "",
    activitiesWithPoints: [],
  });
  const [kpiActivities, setKpiActivities] = useState([]);
  const [initialValues, setInitialValues] = useState({
    name: "",
    description: "",
    kpiActivities: [],
  });

  const isTemplateNameUnique = async (newName) => {
    try {
      let isUnique = false;
      const response = await postAPICall(CHECK_IF_KPI_TEMPLATE_NAME_IS_UNIQUE, true, {
        name: newName,
      });
      if (response?.data?.success) {
        isUnique = response.data.data.isUnique;
      }
      return isUnique;
    } catch (error) {
      return false;
    }
  };

  const fetchFormValues = async () => {
    setLoading(true);
    const [templateData, kpiData] = await Promise.all([
      getAPICall(`${FETCH_SINGLE_KPI_TEMPLATE}/${selectedTemplateId}`, true),
      getAPICall(FETCH_KPI_ACTIVITIES, true),
    ]);
    if (templateData?.data?.success && kpiData?.data?.success) {
      const { template, userAssignedToTemplate, activitiesWithPoints } = templateData.data.data;
      setTemplateDetail({
        template,
        users: userAssignedToTemplate,
        activitiesWithPoints,
      });
      setKpiActivities(kpiData.data.data);

      setInitialValues({
        name: `Copy of ${template?.name}`,
        description: template?.description,
        kpiActivities: activitiesWithPoints.map((kpi) => ({
          ...kpi,
          name: kpi?.KpiActivity?.name,
          id: kpi?.KpiActivity?.id,
        })) || [{ id: null, points: 1, name: "", count: 1, new: false }],
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchFormValues();
  }, []);

  // eslint-disable-next-line func-names
  Yup.addMethod(Yup.array, "unique", function (message, mapper = (a) => a) {
    // eslint-disable-next-line react/no-this-in-sfc
    return this.test("unique", message, (list) => list.length === new Set(list.map(mapper)).size);
  });
  const validationSchema = Yup.object({
    name: Yup.string()
      .max(30, "Name cannot be more than 30 characters")
      .required("Name is required")
      .test("unique-name", "KPI template with that name already exists", async (value) => {
        const isUnique = await isTemplateNameUnique(value);
        if (!isUnique) {
          throw new Yup.ValidationError(
            "KPI template with that name already exists",
            value,
            "name"
          );
        }
        return true;
      }),
    description: Yup.string().max(255, "Description cannot be more than 255 characters"),
    kpiActivities: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().nullable(),
          name: Yup.string().required("Please select KPI").typeError("Please select KPI"),
          points: Yup.number()
            .min(1, "Invalid point value")
            .max(999, "Invalid point value")
            .typeError("Invalid point value"),
          count: Yup.number()
            .min(1, "Invalid KPI value")
            .max(999, "Invalid KPI value")
            .typeError("Invalid KPI value"),
        })
      )
      .unique("New KPI names cannot be same.", (a) => a?.name?.trim())
      .required("Please select KPI Activity"),
  });

  const handleSubmit = async (data) => {
    setIsRequestApiLoading(true);
    try {
      const response = await postAPICall(CREATE_KPI_TEMPLATE, true, {
        ...data,
        assignedUsers: [user],
      });
      if (response?.data?.success) {
        showNotification("success", "Template copied successfully!");
        refetchKpiEntries();
      } else {
        showNotification("error", "Could not copy template, please try again!");
      }
    } catch (error) {
      showNotification("error", "Could not copy template, please try again!");
    } finally {
      setIsRequestApiLoading(false);
      handleClose();
    }
  };

  const handleBackAction = () => {
    setCopying(false);
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Box sx={{ mb: "1.5rem" }}>
        <Typography variant="h4" sx={{ mb: "0.5rem" }}>
          Copy KPI Template
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: "400" }}>
          {`Copying template ${templateDetail?.template?.name || ""}`}
        </Typography>
      </Box>
      <Divider />
      <Box>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validateOnBlur
          validateOnChange={false}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, touched, errors, setValues }) => (
            <Form>
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Template Name"
                      variant="standard"
                      defaultValue={values.name}
                      onChange={(event) => setFieldValue("name", event.target.value)}
                      error={Boolean(touched.name && errors.name)}
                      helperText={touched.name && errors.name}
                      // sx={{ width: isMobileDisplay ? "100%" : "50%" }}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 30 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Description"
                      variant="standard"
                      defaultValue={values.description}
                      onChange={(event) => setFieldValue("description", event.target.value)}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                      // sx={{ width: isMobileDisplay ? "100%" : "50%" }}
                      sx={{ width: "100%" }}
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ mt: "1.75rem" }}>
                <EditKPITable
                  kpiActivities={kpiActivities}
                  setFieldValue={setFieldValue}
                  setValues={setValues}
                  values={values}
                  touched={touched}
                  errors={errors}
                  isMobileDisplay={isMobileDisplay}
                />
              </Box>
              <Divider />
              <MDBox mt={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <MDButton onClick={handleBackAction} color="gray80" style={{ marginRight: "10px" }}>
                  Back
                </MDButton>
                <MDButton color="primaryRed" type="submit" disabled={isRequestApiLoading}>
                  {isRequestApiLoading ? "Loading..." : "Copy and Assign"}
                </MDButton>
              </MDBox>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
}

export default KpiTemplateModalCopy;
